import { StrictMode, useMemo, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider, ChatProvider, ChatWidget, useAuth, AuthBootstrapper, LocaleProvider, initSentry, Sentry } from '@org/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createTrpcClient, trpc } from '@org/clients/school'
import { createGraphTrpcClient, graphTrpc } from '@org/clients/graph'
import { createCommunityTrpcClient, communityTrpc } from '@org/clients/community'
import superjson from 'superjson'
import { installDomResilience } from '@org/utils-client'

import { routeTree } from './routeTree.gen'

import './styles.css'
import { ensureTeacherI18n } from './i18n'

installDomResilience();

initSentry({
  app: 'teacher',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENV ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
});

function RootErrorFallback() {
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2>Something went wrong</h2>
      <p>The page crashed. Our team has been notified. Please refresh to try again.</p>
    </div>
  );
}

const router = createRouter({ routeTree, scrollRestoration: true })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function App() {
  const { getAccessToken } = useAuth();
  const [queryClient] = useState(() => new QueryClient());
  
  const trpcClient = useMemo(() => {
    return createTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:3333/api',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const graphClient = useMemo(() => {
    return createGraphTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_GRAPH_BACKEND_URL ?? 'http://localhost:3334/graph',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  const communityClient = useMemo(() => {
    return createCommunityTrpcClient({
      queryClient,
      getAccessToken,
      apiUrl: import.meta.env.VITE_COMMUNITY_BACKEND_URL ?? 'http://localhost:3333/community',
      transformer: superjson,
    });
  }, [queryClient, getAccessToken]);

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <graphTrpc.Provider client={graphClient} queryClient={queryClient}>
        <communityTrpc.Provider client={communityClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AuthBootstrapper />
            <LocaleProvider>
              <ChatProvider>
                <Sentry.ErrorBoundary fallback={<RootErrorFallback />}>
                  <RouterProvider router={router} />
                </Sentry.ErrorBoundary>
                <ChatWidget />
              </ChatProvider>
            </LocaleProvider>
          </QueryClientProvider>
        </communityTrpc.Provider>
      </graphTrpc.Provider>
    </trpc.Provider>
  );
}

const renderApp = () => {
  const rootElement =
    typeof globalThis !== 'undefined' && (globalThis as Record<string, any>).document
      ? (globalThis as Record<string, any>).document.getElementById('root')
      : null;
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <StrictMode>
        <AuthProvider>
          <App />
        </AuthProvider>
      </StrictMode>,
    );
  }
};

ensureTeacherI18n()
  .then(renderApp)
  .catch((error) => {
    console.error('Failed to initialize translations', error);
    renderApp();
  });
